import { useContext, useState } from 'react';
import useLocalStorage from 'hooks/useLocalStorage';
import { PropertyBusinessType } from 'models/Properties';
import CalendarContext from '../CalendarContext';

/**
 * This hook is used to persist the expansion state of a property in the local storage.
 *
 * Hotels are the only entity to be expanded by default.
 */
const usePersistentPropertyExpansion = (propertyUid: string) => {
  const { getValue, setValue } = useLocalStorage<Record<string, boolean>>(
    'stackedCalendar_expandedProperties',
    {},
  );
  const { propertiesMap } = useContext(CalendarContext);
  const isHotel =
    propertiesMap[propertyUid].businessType === PropertyBusinessType.HOTEL;
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    const expandedProperties = getValue() || {};

    // if there was no hotel expansion state, meaning it wasn't clicked yet, we need to expand it by default
    return expandedProperties[propertyUid] === undefined && isHotel
      ? true
      : expandedProperties[propertyUid] || false;
  });

  const togglePropertyExpansion = () => {
    const expandedProperties = getValue() || {};
    const isPropertyExpanded = expandedProperties[propertyUid];

    if (isHotel && isPropertyExpanded === undefined) {
      // undefined means that hotel wasn't clicked yet AND hotel is expanded by default, so we need to collapse it
      setValue({ ...expandedProperties, [propertyUid]: false });
      setIsExpanded(false);
      return;
    }

    setValue({ ...expandedProperties, [propertyUid]: !isPropertyExpanded });
    setIsExpanded(!isPropertyExpanded);
  };

  return { togglePropertyExpansion, isExpanded };
};

export default usePersistentPropertyExpansion;
